// Contrôles des champs d'inscription, alignés sur les contraintes de l'API
// (app/schemas/user.py). Chaque écran vérifie AVANT d'avancer : une valeur hors
// bornes ne se découvrait jusqu'ici qu'au tout dernier écran, sous la forme d'un 422
// que personne ne voyait — l'inscription échouait sans explication.
//
// Les validateurs renvoient un message d'erreur, ou '' si la valeur est acceptable.

export const HEIGHT_MIN = 100;
export const HEIGHT_MAX = 250;
export const WEIGHT_MIN = 30;
export const WEIGHT_MAX = 200;
export const NAME_MAX = 50;
export const SPORT_MAX_LENGTH = 255;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Lettres (accents compris), espaces, tirets et apostrophes : « Marie-Ève », « N'Diaye ».
const NAME_RE = /^\p{L}[\p{L}\p{M}' ’-]*$/u;

/** Nombre décimal saisi au clavier français ou anglais : « 55,5 » comme « 55.5 ». */
export function parseDecimal(raw) {
  const s = String(raw ?? '').trim().replace(',', '.').replace(/\s/g, '');
  if (!s) return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return NaN;
  return Number(s);
}

export function validateEmail(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return "L'email est requis.";
  if (!EMAIL_RE.test(v)) return "Cette adresse email n'est pas valide.";
  return '';
}

export function validateName(raw, label) {
  const v = String(raw ?? '').trim();
  if (v.length < 2) return `${label} est requis (2 caractères minimum).`;
  if (v.length > NAME_MAX) return `${label} ne peut dépasser ${NAME_MAX} caractères.`;
  if (!NAME_RE.test(v)) return `${label} ne peut contenir que des lettres, espaces, tirets et apostrophes.`;
  return '';
}

/**
 * Taille en cm. Une taille saisie en mètres (« 1,65 ») est convertie : c'est
 * l'erreur la plus probable, et elle envoyait `taille: 1` → refus de l'API.
 */
export function normalizeHeight(raw) {
  const n = parseDecimal(raw);
  if (n === null || Number.isNaN(n)) return n;
  return n > 0 && n < 3 ? Math.round(n * 100) : Math.round(n);
}

export function validateHeight(raw) {
  const n = normalizeHeight(raw);
  if (n === null) return '';
  if (Number.isNaN(n)) return 'Saisissez votre taille en chiffres, en centimètres (ex : 165).';
  if (n < HEIGHT_MIN || n > HEIGHT_MAX) {
    return `La taille doit être comprise entre ${HEIGHT_MIN} et ${HEIGHT_MAX} cm.`;
  }
  return '';
}

export function normalizeWeight(raw) {
  const n = parseDecimal(raw);
  if (n === null || Number.isNaN(n)) return n;
  return Math.round(n * 10) / 10;
}

export function validateWeight(raw) {
  const n = normalizeWeight(raw);
  if (n === null) return '';
  if (Number.isNaN(n)) return 'Saisissez votre poids en chiffres, en kilos (ex : 58,5).';
  if (n < WEIGHT_MIN || n > WEIGHT_MAX) {
    return `Le poids doit être compris entre ${WEIGHT_MIN} et ${WEIGHT_MAX} kg.`;
  }
  return '';
}

/** Date `YYYY-MM-DD` → Date locale, ou null si absente/invalide. */
function parseISODate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}

const startOfToday = () => {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
};

/** Dates de cycle déclarées : facultatives, dans l'année écoulée, jamais futures. */
export function validateCycleDate(iso) {
  if (!iso) return '';
  const d = parseISODate(iso);
  if (!d) return 'Date invalide.';
  const today = startOfToday();
  if (d > today) return 'La date ne peut pas être dans le futur.';
  const oneYearAgo = new Date(today);
  oneYearAgo.setDate(oneYearAgo.getDate() - 365);
  if (d < oneYearAgo) return "La date doit dater de moins d'un an.";
  return '';
}

/** Date de naissance : passée et plausible (entre 10 et 100 ans). */
export function validateBirthDate(date) {
  if (!date) return 'La date de naissance est requise.';
  const d = date instanceof Date ? date : parseISODate(date);
  if (!d) return 'Date invalide.';
  const today = startOfToday();
  if (d >= today) return 'La date de naissance doit être dans le passé.';
  const age = (today - d) / (365.25 * 24 * 3600 * 1000);
  if (age < 10) return "Vérifiez l'année : CycleGala s'adresse aux sportives de 10 ans et plus.";
  if (age > 100) return "Vérifiez l'année de naissance saisie.";
  return '';
}

/** `sport` est une colonne de 255 caractères : on garde autant de sports entiers que possible. */
export function joinSportsForApi(sports) {
  if (!sports?.length) return null;
  let out = '';
  for (const s of sports) {
    const next = out ? `${out}, ${s}` : s;
    if (next.length > SPORT_MAX_LENGTH) break;
    out = next;
  }
  return out || null;
}
